import { useRef, useEffect, useState, Children, cloneElement } from "react";

/**
 * Slowly auto-scrolls children horizontally. Pauses on hover.
 * Duplicates children once for a seamless loop.
 */
export default function AutoScrollRow({ children, speed = 0.35, className = "" }) {
  const scrollRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let rafId;
    const tick = () => {
      if (!paused && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += speed;
        const half = el.scrollWidth / 2;
        if (el.scrollLeft >= half) {
          el.scrollLeft = 0;
        }
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [paused, speed]);

  const items = Children.toArray(children);
  const loop = [...items, ...items];

  return (
    <div
      ref={scrollRef}
      className={`flex gap-6 overflow-x-hidden pb-4 select-none ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      aria-label="Auto-scrolling modules"
    >
      {loop.map((child, index) =>
        cloneElement(child, {
          key: `${child.key ?? child.props?.["data-title"] ?? index}-${index}`,
        }),
      )}
    </div>
  );
}
